import './ensure-runtime-config'
import PrimeVue from 'primevue/config'
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { setupCalendar } from 'v-calendar'
import router from './router'
import App from './App.vue'
import store from './store/index'
import { useAuthStore } from './pinia/auth'
import i18n from './i18n'
import components from './components'
import { captureError, initPosthog, posthogEnabled } from './posthog'
import CookieConsentPlugin from './cookie-consent'
import './styles/base.scss'
import './styles/mapbox.scss'
import './styles/mixins.scss'
import './styles/navvis.scss'

initPosthog()

if (posthogEnabled) {
  globalThis.addEventListener('error', (event) => {
    captureError(event.error ?? event.message, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno
    })
  })

  globalThis.addEventListener('unhandledrejection', (event) => {
    captureError(event.reason)
  })

  // Vite fires this when a lazy chunk's preload fails; the import() that
  // follows may be caught somewhere and never reach the handlers above
  globalThis.addEventListener('vite:preloadError', (event) => {
    captureError((event as Event & { payload: unknown }).payload, { source: 'vite_preload' })
  })

  // Failed lazy route imports and throwing guards end up here. Without a
  // listener vue-router only logs them, e.g. on the initial navigation
  router.onError((error, to) => {
    captureError(error, { source: 'router', route_name: to.name, route_path: to.path })
  })
}

/**
 * Summary: Pinia is used for app state management
 */
const pinia = createPinia()

const app = createApp(App)
if (posthogEnabled) {
  app.config.errorHandler = (err, instance, info) => {
    captureError(err, {
      source: 'vue',
      vue_component: instance?.$options.name ?? instance?.$options.__name,
      vue_info: info
    })
  }
}
app.use(CookieConsentPlugin)
app.use(PrimeVue, { unstyled: true })
app.use(pinia)
useAuthStore().initialize()
app.use(store)
app.use(router)
app.use(i18n)
app.use(setupCalendar, {})
Object.entries(components).forEach(([name, component]) => {
  app.component(name, component)
})
app.mount('#app')
